import React, { useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import { StationProvisioning } from "../../../../hooks/useStationProvisioning";
import { manualActionGuidance, missingLabel } from "../../../../utils/eroundProvisioning";
import ProvisioningCell from "./ProvisioningCell";
import ProvisioningDialog from "./ProvisioningDialog";
import { provisioningErrorMessage } from "./provisioningErrors";

const formatDate = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
};

/** One line on where a station stands in eRound provisioning, in ops' words. */
export const provisioningSummary = (entry?: StationProvisioning): string => {
  if (!entry || entry.state === "loading") return "";
  if (entry.state === "unavailable") return provisioningErrorMessage(entry.error, "load");
  const record = entry.record;
  if (!record) return "Not handled by the automation: set it up in eRound by hand.";
  switch (record.status) {
    case "COLLECTING":
      return `Missing: ${record.missing.map(missingLabel).join("; ")}.`;
    case "AWAITING_APPROVAL":
      return "Ready. Waiting for your approval.";
    case "DISPATCHED":
    case "IN_PROGRESS":
      return "Being created in eRound.";
    case "SUCCEEDED":
      return `Created in eRound ${formatDate(record.lastResult?.finishedAt)}.`.replace(" .", ".");
    case "FAILED":
      return record.lastResult?.reason ?? "Failed without a reason.";
    case "NEEDS_MANUAL_ACTION":
      return manualActionGuidance(record.manualActionReason);
    case "REJECTED":
      return `Rejected${record.rejection?.reason ? `: ${record.rejection.reason}` : ""}. Set it up in eRound by hand.`;
    default:
      return "";
  }
};

interface EroundProvisioningSectionProps {
  stationIds: string[];
  provisioning: { byStation: Record<string, StationProvisioning>; reload: () => Promise<unknown> | void };
}

/** eRound provisioning on the prospect's Progress tab (SOPS-148). */
const EroundProvisioningSection: React.FC<EroundProvisioningSectionProps> = ({ stationIds, provisioning }) => {
  const [reviewing, setReviewing] = useState<string | null>(null);
  const reviewed = reviewing ? provisioning.byStation[reviewing] : undefined;

  if (!stationIds.length) return null;

  return (
    <div className="mt-4">
      <h3 className="mt-0">eRound provisioning</h3>
      <DataTable value={stationIds.map((stationId) => ({ stationId }))} dataKey="stationId">
        <Column header="Station" field="stationId" />
        <Column
          header="Status"
          body={({ stationId }: { stationId: string }) => (
            <ProvisioningCell
              stationId={stationId}
              entry={provisioning.byStation[stationId]}
              onReview={() => setReviewing(stationId)}
              onChanged={provisioning.reload}
            />
          )}
        />
        <Column
          header="What's happening"
          body={({ stationId }: { stationId: string }) => provisioningSummary(provisioning.byStation[stationId])}
        />
      </DataTable>
      {reviewed?.state === "loaded" && reviewed.record && (
        <ProvisioningDialog
          record={reviewed.record}
          visible
          onHide={() => setReviewing(null)}
          onChanged={provisioning.reload}
        />
      )}
    </div>
  );
};

export default EroundProvisioningSection;
